import Alert from 'components/alert'
import AppButton from 'components/app-button'
import { LoadingIcon } from 'components/icons'
import InputLineItem from 'components/input-line-item'
import Modal from 'components/modal'
import useForm from 'hooks/use-form'
import { __ } from 'lang'
import { groupBy, times } from 'lodash'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import ReturnsService from 'services/returns-service'
import { useMount } from 'utils'
import * as yup from 'yup'
import { LineItemModel, matchReason } from '../pages/request'

export default function SummaryEditForm({ page }) {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [showModal, toggleModal] = useState(false)
  const [reasons, setReasons] = useState([])
  const [orders, setOrders] = useState([])
  const [editable, setEditable] = useState(true)

  const form = useForm({
    request: ReturnsService.editReturns,

    defaults: {
      line_items: [],
    },

    rules: {
      line_items: yup.array().of(
        yup.object({
          should_return: yup.boolean(),

          reason: yup.string().when('should_return', {
            is: true,
            then: () => yup.string().required(),
          }),

          other_reason: yup.string().when('reason', {
            is: (reason) => matchReason(['OTHER'], reason),
            then: () => yup.string().required(),
          }),

          description: yup.string().when('reason', {
            is: (reason) => matchReason(['FAULT', 'FAULTY'], reason),
            then: () => yup.string().required(),
          }),

          current_images: yup.array(),

          files: yup.array().when(['reason', 'current_images'], {
            is: (reason, images) =>
              matchReason(['FAULT', 'FAULTY', 'WRONG ITEM RECEIVED'], reason) && images.length === 0,
            then: () =>
              yup
                .array()
                .min(1, 'Must upload at least one photo')
                .max(5, 'Must upload not more than five photos'),
          }),
        }),
      ),
    },

    transform: (originalData) =>
      Object.values(groupBy(originalData.line_items)).map((lineItems) => ({
        return_hash: page.orderReturn.return_hash,
        sales_order_id: lineItems[0].sales_order_id,
        line_items: lineItems.map((li) => ({
          qty_to_return: li.should_return ? li.qty_to_return : 0,
          middleware_item_id: li.middleware_item_id,
          description: li.description,
          reason_to_return:
            String(li.reason).toUpperCase() === 'OTHER'
              ? li.other_reason
              : li.reason,
          sales_order_id: li.sales_order_id,
          sku: li.sku,
          status: 'open',
        })),
      })),

    onSuccess: async () => {
      const toUpload = form.data.line_items
        .filter((item) => item.qty_to_return > 0 && item.should_return)
        .filter(({ reason }) => matchReason(['FAULT', 'FAULTY', 'WRONG ITEM RECEIVED'], reason))
        .filter(({ files }) => files.length > 0)

      for (const item of toUpload) {
        const formData = new FormData()
        formData.append('return_hash', page.orderReturn.return_hash)
        formData.append('middleware_item_id', item.middleware_item_id)
        item.files.forEach((file, i) => formData.append(`files[${i}]`, file))
        await ReturnsService.addImagesToItem(formData)
      }

      navigate(0)
    },
  })

  useMount(() => {
    setLoading(true)

    Promise.all([
      ReturnsService.getReasons(),
      ReturnsService.getReturnDetails({ return_hash: page.orderReturn.return_hash }),
    ]).then(([_reasons, _details]) => {
      setReasons(_reasons.data.data)
      setOrders(_details.data.data.orders)

      if ('isEditable' in _details.data.data) {
        setEditable(_details.data.data.isEditable)
      }

      const newLineItems = _details.data.data.orders
        .reduce((prev, curr) => [...prev, ...Object.values(curr.line_items)], [])
        .map(LineItemModel)

      newLineItems.forEach((item) => {
        const itemToReturn = page.orderReturn.line_items.find((li) => li.middleware_item_id === item.middleware_item_id)

        if (!itemToReturn) return

        const isOtherReason = !matchReason(
          _reasons.data.data.map((r) => r.reason),
          itemToReturn.reason_to_return,
        )

        item.qty_to_return = itemToReturn.qty_to_return
        item.description = itemToReturn.description || ''
        item.reason = isOtherReason ? 'OTHER' : itemToReturn.reason_to_return
        item.other_reason = isOtherReason ? itemToReturn.reason_to_return : ''
        item.current_images = item.images ? JSON.parse(item.images) : []
        item.should_return = true
      })

      form.setData({
        line_items: newLineItems,
      })

      setLoading(false)
    })
  })

  return (
    <div>
      {editable && (
        <AppButton onClick={() => toggleModal(true)} className="px-2 py-1 text-xs uppercase tracking-widest">
          Edit
        </AppButton>
      )}
      <Modal
        onClose={() => toggleModal(false)}
        panelClassName="sm:max-w-2xl"
        show={showModal}
        title="Edit return request"
        actions={
          <>
            <AppButton
              className="w-full px-4 py-3 text-xs uppercase tracking-widest sm:w-auto"
              color="primary"
              disabled={form.processing || loading}
              onClick={() => form.submit()}
            >
              <span>{form.processing ? 'Saving' : 'Save'}</span>
              {form.processing && <LoadingIcon className="ml-2 h-4 w-4 animate-spin text-white" />}
            </AppButton>
            <AppButton
              className="w-full px-4 py-3 text-xs uppercase tracking-widest sm:w-auto"
              onClick={() => toggleModal(false)}
            >
              Cancel
            </AppButton>
          </>
        }
      >
        {form.error && (
          <Alert
            className="mt-8"
            description={__(form.error.data?.code || 'alert_error_default_description')}
            title={__('alert_error_default_title')}
            variant="danger"
            tabIndex="-1"
          />
        )}

        {!loading ? (
          <form className="mt-8 space-y-10" onSubmit={form.submit}>
            {form.data.line_items.map((item, i) => (
              <InputLineItem
                errors={{
                  reason: form.errors[`line_items[${i}].reason`],
                  other_reason: form.errors[`line_items[${i}].other_reason`],
                  files: form.errors[`line_items[${i}].files`],
                  description: form.errors[`line_items[${i}].description`],
                }}
                form={form}
                item={item}
                key={item.id}
                orders={orders}
                reasons={reasons}
                size="sm"
              />
            ))}
          </form>
        ) : (
          <div className="mt-10 space-y-10">
            <div className="space-y-2">
              <div className="h-5 w-64 animate-pulse bg-gray-200"></div>
              <div className="h-5 w-24 animate-pulse bg-gray-200"></div>
            </div>
            {times(2).map((key) => (
              <div key={key} className="flex flex-wrap items-start">
                <div className="aspect-[1/1.5] w-full animate-pulse bg-gray-200 sm:w-72"></div>
                <div className="mt-6 flex-1 sm:mt-0 sm:ml-6">
                  <div className="h-5 w-2/3 animate-pulse bg-gray-200"></div>
                  <div className="mt-4 h-5 w-1/3 animate-pulse bg-gray-100"></div>
                  <div className="mt-4 h-5 w-1/4 animate-pulse bg-gray-100"></div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  )
}
